import { estimateDiscountFromJSON } from "common/records/EstimateDiscount";
import { IEstimateGroupData } from "common/records/EstimateGroup";
import { estimatePaymentTermsFromJSON, IEstimatePaymentTermsData } from "common/records/EstimatePaymentTerms";
import { IEstimatePaymentTermItemData, estimatePaymentTermItemFromJSON } from "common/records/PaymentTermItem";
import { RootActions, RootState } from "common/reducers";
import {
  ADD_CUSTOM_ESTIMATE_LINE_ITEM,
  ADD_ESTIMATE_DISCOUNT,
  ADD_ESTIMATE_FINANCE_OPTION,
  ADD_ESTIMATE_GROUP,
  ADD_ESTIMATE_LINE_ITEM,
  ADD_ESTIMATE_LINE_ITEM_OPTION,
  ADD_ESTIMATE_PAYMENT_TERM,
  ADD_ESTIMATE_PAYMENT_TERM_ITEM,
  APPLY_ESTIMATE_LINE_ITEM_DEFAULTS,
  APPLY_ESTIMATE_TEMPLATE,
  CANCEL_LINE_ITEM_SESSION,
  COMMIT_LINE_ITEM_SESSION,
  CLEANUP_DRAFT_LINE_ITEMS,
  RECALCULATE_ESTIMATE_TOTALS,
  COMMIT_GROUP_TOTALS_TO_LAST_SAVED,
  CREATE_ESTIMATE,
  DEFAULT_ESTIMATE_GROUPS_CONFIG,
  UPDATE_ESTIMATE_LINE_ITEM_FIELD,
  DUPLICATE_ESTIMATE,
  IEstimateGroupConfig,
  LINE_ITEM_DRAG_END,
  MOVE_ESTIMATE_LINE_ITEM,
  REMOVE_ESTIMATE_DISCOUNT,
  REMOVE_ESTIMATE_FINANCE_OPTION,
  REMOVE_ESTIMATE_GROUP,
  REMOVE_ESTIMATE_LINE_ITEM,
  REMOVE_ESTIMATE_LINE_ITEM_OPTION,
  REMOVE_ESTIMATE_PAYMENT_TERM_ITEM,
  REORDER_ESTIMATE_GROUP,
  REORDER_GROUPS_IN_BLOCK,
  REORDER_ESTIMATE_LINE_ITEM,
  SET_ESTIMATE_LINE_ITEM_PRODUCT,
  SET_LINE_ITEM_OPENING_ESTIMATIONS,
  APPLY_WINDOW_PRICE_ADJUSTMENT,
  CHANGE_LINE_ITEM_PRODUCT_WITH_OPTIONS,
  START_ADDING_LINE_ITEM,
  START_LINE_ITEM_SESSION,
  MARK_LINE_ITEM_DRAFT,
  UPDATE_ESTIMATE,
  UPDATE_ESTIMATE_DISCOUNT,
  UPDATE_ESTIMATE_GROUP,
  UPDATE_ESTIMATE_LINE_ITEM,
  UPDATE_ESTIMATE_LINE_ITEM_DEFAULTS,
  UPDATE_ESTIMATE_LINE_ITEM_OPTION,
  UPDATE_ESTIMATE_PAYMENT_TERM_ITEM,
  UPDATE_SALES_TAX,
  RESET_ESTIMATE,
  PATCH_LINE_ITEM_VALIDATIONS,
  EstimateEditorActions,
} from "common/reducers/estimateEditor.actions";
import { visibleIndexToFullIndex, renumberEstimateGroupSortOrders } from "common/helpers/estimateEditorHelpers";
import { EstimateActions } from "common/reducers/estimate.actions";
import { reducer as estimateReducer } from "common/reducers/estimate.reducer";
import { EstimateGroupActions } from "common/reducers/estimateGroup.actions";
import { estimateGroupsReducer } from "common/reducers/estimateGroup.reducer";
import { EstimateLineItemActions } from "common/reducers/estimateLineItem.actions";
import { estimateLineItemsReducer } from "common/reducers/estimateLineItem.reducer";
import { EstimateLineItemOptionActions } from "common/reducers/estimateLineItemOption.actions";
import { estimateLineItemOptionsReducer } from "common/reducers/estimateLineItemOption.reducer";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { reducer as rsfFormReducer } from "common/reducers/rsfForm.reducer";
import { updateNodeAndParents } from "common/helpers/EstimateTree";
import { discount as discountSelector } from "common/selectors/discount.selectors";
import {
  unsavedId as estimateUnsavedIdSelector,
  estimate as estimateSelector,
} from "common/selectors/estimate.selectors";
import {
  unsavedId as estimateGroupUnsavedIdSelector,
  estimateGroup as estimateGroupSelector,
} from "common/selectors/estimateGroup.selectors";
import {
  unsavedId as estimateLineItemUnsavedIdSelector,
  estimateLineItem as estimateLineItemSelector,
  byId as lineItemsById,
} from "common/selectors/estimateLineItem.selectors";
import {
  unsavedId as estimateLineItemOptionUnsavedIdSelector,
  estimateLineItemOption as estimateLineItemOptionSelector,
} from "common/selectors/estimateLineItemOption.selectors";
import { currentOrg, currentOrgPrefConfig } from "common/selectors/org.selectors";
import { paymentTerms, paymentTerm } from "common/selectors/paymentTerm.selectors";
import { productById, productOverviewById } from "common/selectors/product.selectors";
import { ProductRecord } from "common/records/Product";
import {
  deriveLineItemQuantity,
  productLineItemFields,
  applyDefaultOptions as applyDefaultOptionsHelper,
  applyDefaultOptionsFromList,
  DefaultOptionToAdd,
} from "common/helpers/ProductLineItem";
import { financeOption } from "common/selectors/financeOption.selectors";
import { EstimateFinanceOptionRecord } from "common/records/EstimateFinanceOption";
import { reducer as estimateFinanceOptionReducer } from "common/reducers/estimateFinanceOption.reducer";
import { EstimateFinanceOptionActions } from "common/reducers/estimateFinanceOption.actions";
import {
  byId as estimateFinanceOptionsById,
  lastSavedById as estimateFinanceOptionsLastSavedById,
  unsavedId as estimateFinanceOptionUnsavedIdSelector,
} from "common/selectors/estimateFinanceOption.selectors";
import { productOptionById, productOptionByMatchId } from "common/selectors/productOption.selectors";
import { imageUrlsFromIds } from "common/selectors/image.selectors";
import { productOptionGroupById } from "common/selectors/productOptionGroup.selectors";
import { List, Map } from "immutable";
import { defaultOptionFromJSON, DefaultOptionRecord } from "common/records/DefaultOption";
import { defaultsFromJSON } from "common/records/Defaults";
import { OpeningEstimationRecord } from "common/records/OpeningEstimation";
import { windowPriceAdjustment } from "common/records/EstimateLineItem";
import { OpeningEstimationActions } from "common/reducers/openingEstimation.actions";
import { openingEstimationReducer } from "common/reducers/openingEstimation.reducer";
import type { OpeningRecord } from "common/records/Opening";

// Opening estimations are read inline from the normalized slice (not via openingEstimation.selectors)
// to avoid a module-init cycle through measurement.common.selectors (see APPLY_WINDOW_PRICE_ADJUSTMENT).
const openingEstimationById = (state: RootState, id: number): OpeningEstimationRecord | undefined =>
  state.getIn(["openingEstimations", "byId", id]) as OpeningEstimationRecord | undefined;
const openingEstimationUnsavedId = (state: RootState): number =>
  state.getIn(["openingEstimations", "unsavedId"]) as number;

// Resolves the product to copy onto a line item. productOverviewById is a flat id lookup into the
// global overview store, resolving any depth (roots/branches/leaves) — productsForActivatedPriceList
// is roots-only and would miss non-root selections. `products.byId` is a fallback for products
// loaded individually via /api/v1/products.
const resolveProductForLineItem = (state: RootState, productId: number): ProductRecord | null => {
  return productOverviewById(state, productId) ?? productById(state, productId) ?? null;
};

// Thin wrapper passing this module's `reducer` to the shared helper, which replays the
// ADD_ESTIMATE_LINE_ITEM_OPTION action through it (avoids a circular helper→reducer import).
const applyDefaultOptions = (state: RootState, lineItemId: number, product: ProductRecord): RootState =>
  applyDefaultOptionsHelper(state, lineItemId, product, reducer);

export const reducer = (state: RootState, action: RootActions): RootState => {
  switch (action.type) {
    case CREATE_ESTIMATE: {
      const { jobId, kind, groupsConfig, activatedPriceListId } = action.payload;

      const org = currentOrg(state);
      if (!org) {
        console.error("Cannot create estimate: no current org found");
        return state;
      }

      const estimateId = estimateUnsavedIdSelector(state);
      state = estimateReducer(state, EstimateActions.decrementUnsavedId());

      const salesTax = currentOrgPrefConfig(state, { path: ["sales_tax"] });
      const markup = currentOrgPrefConfig(state, { path: ["markup"] });
      const estimatorConfig = currentOrgPrefConfig(state, { path: ["estimator"] });

      const defaultTaxRate = salesTax?.get?.("default_rate") ?? 0;
      const defaultTaxableAmount = salesTax?.get?.("default_taxable_amount") ?? "none";

      // Seed a new estimate with the org's default markup — but only when markup is enabled for the
      // org (mirrors rsf-app EstimatorService.initializeMarkup). The defaults live under the
      // `*_default` keys (MarkupPricing config); reading `product`/`labor` always missed and fell to
      // 0. This is gated on `markup.enabled` alone, never on the `update_markup` permission — that
      // permission gates the markup *controls*, not whether an estimate carries markup.
      const markupIsEnabled = !!markup?.get?.("enabled");
      const productMarkup = markupIsEnabled ? (markup?.get?.("product_default") ?? 0) : 0;
      const laborMarkup = markupIsEnabled ? (markup?.get?.("labor_default") ?? 0) : 0;

      const orgPaymentTerms = paymentTerms(state);
      const firstPaymentTerm = orgPaymentTerms.first();

      const paymentTermsData: Partial<IEstimatePaymentTermsData> = firstPaymentTerm
        ? {
            id: firstPaymentTerm.id,
            name: firstPaymentTerm.name,
            description: firstPaymentTerm.description,
            financed_amount: 0,
            payment_term_items: firstPaymentTerm.breakdown.items
              .map((item) => ({
                uuid: item.uuid,
                amount: item.amount,
                description: item.description,
                type: item.type,
                calculated: 0,
                paymentMethod: item.paymentMethod,
              }))
              .toArray() as IEstimatePaymentTermItemData[],
          }
        : {};

      const estimateData = {
        id: estimateId,
        job_id: jobId,
        kind,
        activated_price_list_id: activatedPriceListId ?? org.activated_price_list_id ?? 0,
        tax_rate: defaultTaxRate,
        taxable_amount: 0,
        default_taxable_amount: defaultTaxableAmount,
        product_markup: productMarkup,
        labor_markup: laborMarkup,
        calc_method: "standard" as const,
        payment_terms: paymentTermsData,
        group_ids: List<number>(),
      };

      state = estimateReducer(state, EstimateActions.setEstimateById(estimateId, estimateData as any));

      let resolvedGroupsConfig: IEstimateGroupConfig[];
      if (groupsConfig && groupsConfig.length > 0) {
        resolvedGroupsConfig = groupsConfig;
      } else {
        const orgGroupsConfig = estimatorConfig?.getIn?.(["groups", kind]);
        if (orgGroupsConfig && List.isList(orgGroupsConfig) && orgGroupsConfig.size > 0) {
          resolvedGroupsConfig = orgGroupsConfig.toJS() as IEstimateGroupConfig[];
        } else {
          resolvedGroupsConfig = DEFAULT_ESTIMATE_GROUPS_CONFIG;
        }
      }

      const groupIds: number[] = [];
      resolvedGroupsConfig.forEach((config, index) => {
        const groupId = estimateGroupUnsavedIdSelector(state);
        state = estimateGroupsReducer(state, EstimateGroupActions.decrementUnsavedId());

        const groupData: Partial<IEstimateGroupData> = {
          id: groupId,
          name: config.name,
          included: config.included,
          estimate_id: estimateId,
          sort_order: index,
          subtotal: 0,
          labor_total: 0,
          product_total: 0,
          discountable_total: 0,
          product_markup: null,
          labor_markup: null,
        };
        state = estimateGroupsReducer(state, EstimateGroupActions.setEstimateGroupById(groupId, groupData));
        groupIds.push(groupId);
      });

      state = state.setIn(["estimates", "byId", estimateId, "group_ids"], List(groupIds));
      state = estimateReducer(state, EstimateActions.copyEstimateToLastSaved(estimateId));

      // Also copy groups to lastSavedById so the new estimate is not immediately dirty
      groupIds.forEach((groupId) => {
        const groupRecord = state.getIn(["estimateGroups", "byId", groupId]);
        if (groupRecord) {
          state = state.setIn(["estimateGroups", "lastSavedById", groupId], groupRecord);
        }
      });

      state = estimateReducer(state, EstimateActions.setCurrentEstimateId(estimateId));

      return state;
    }
    case DUPLICATE_ESTIMATE: {
      const { estimateId } = action.payload;

      const originalEstimate = estimateSelector(state, { estimateId });
      if (!originalEstimate) {
        console.error("Cannot duplicate estimate: estimate not found", { estimateId });
        return state;
      }

      const newEstimateId = estimateUnsavedIdSelector(state);
      state = estimateReducer(state, EstimateActions.decrementUnsavedId());

      // Clear the transient source→clone line-item id map so it holds only this cascade's mappings,
      // which duplicateEstimateAttachments reads to re-upload owned documents onto the clones. (Owned
      // images are cloned server-side via remote_file_url in the duplicate reducer, not re-uploaded.)
      state = estimateLineItemsReducer(state, EstimateLineItemActions.resetDuplicateSourceToCloneMap());

      const duplicatedEstimateName = `Copy of ${originalEstimate.name || "Estimate"}`;

      // Remove one_look from metadata to prevent conflicts with previous financing applications
      let duplicatedMetadata = originalEstimate.metadata;
      if (duplicatedMetadata && duplicatedMetadata.has("one_look")) {
        duplicatedMetadata = duplicatedMetadata.delete("one_look");
      }

      const financeOptionIdsArray = List.isList(originalEstimate.finance_option_ids)
        ? originalEstimate.finance_option_ids.toArray()
        : originalEstimate.finance_option_ids || [];

      const duplicatedEstimateData = {
        ...originalEstimate.toJS(),
        id: newEstimateId,
        name: duplicatedEstimateName,
        finance_option_ids: [],
        metadata: duplicatedMetadata ? duplicatedMetadata.toJS() : {},
        group_ids: [],
      };
      state = estimateReducer(state, EstimateActions.setEstimateById(newEstimateId, duplicatedEstimateData as any));
      state = state.setIn(["estimates", "byId", newEstimateId, "finance_option_ids"], List<number>());
      state = state.setIn(["estimates", "byId", newEstimateId, "group_ids"], List<number>());

      originalEstimate.group_ids.forEach((sourceGroupId) => {
        state = estimateGroupsReducer(state, EstimateGroupActions.duplicateEstimateGroup(sourceGroupId, newEstimateId));
      });

      financeOptionIdsArray.forEach((sourceEstimateFinanceOptionId: number) => {
        state = estimateFinanceOptionReducer(
          state,
          EstimateFinanceOptionActions.duplicateEstimateFinanceOption(sourceEstimateFinanceOptionId, newEstimateId),
        );
      });

      state = estimateReducer(state, EstimateActions.copyEstimateToLastSaved(newEstimateId));

      return state;
    }
    case APPLY_ESTIMATE_TEMPLATE: {
      const { estimateId, template, mode } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot apply template: estimate not found", { estimateId });
        return state;
      }

      // Mode pre-pass (reuses REMOVE_ESTIMATE_GROUP — hard-deletes unsaved groups, marks saved ones
      // _destroy — so estimate-level config like discounts/payment terms/sales tax/markup is kept):
      //  - replace: remove ALL existing groups first (rsf-app replaceEstimate).
      //  - merge: sweep groups with no live line items first, so non-matching empty scaffold groups
      //    don't linger after the merge (rsf-app mergeEstimate).
      const groupIdsBeforeApply =
        (state.getIn(["estimates", "byId", estimateId, "group_ids"]) as List<number>) ?? List<number>();
      groupIdsBeforeApply.forEach((groupId) => {
        const group = estimateGroupSelector(state, { estimateGroupId: groupId });
        if (!group || group._destroy) return;
        if (mode === "replace") {
          state = reducer(state, EstimateEditorActions.removeEstimateGroup(estimateId, groupId));
          return;
        }
        const liveLineItemCount = group.line_item_ids.filter((lineItemId) => {
          const lineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
          return !!lineItem && !lineItem._destroy;
        }).size;
        if (liveLineItemCount === 0) {
          state = reducer(state, EstimateEditorActions.removeEstimateGroup(estimateId, groupId));
        }
      });

      template.groups.forEach((templateGroup) => {
        let targetGroupId: number | undefined;

        const currentEstimateGroupIds = state.getIn(["estimates", "byId", estimateId, "group_ids"]) as List<number>;

        // Match an existing group by name AND included (a template "Options"/excluded group must not
        // merge into an estimate "Options"/included group) — strict rsf-app parity.
        const existingGroupId = currentEstimateGroupIds.find((groupId) => {
          const group = estimateGroupSelector(state, { estimateGroupId: groupId });
          return !!(
            group &&
            !group._destroy &&
            group.name === templateGroup.name &&
            group.included === templateGroup.included
          );
        });

        if (existingGroupId !== undefined) {
          targetGroupId = existingGroupId;
        } else {
          const newGroupId = estimateGroupUnsavedIdSelector(state);
          state = estimateGroupsReducer(state, EstimateGroupActions.decrementUnsavedId());

          const currentGroupIds = state.getIn(["estimates", "byId", estimateId, "group_ids"]) as List<number>;
          const sortOrder = currentGroupIds.size;

          const newGroupData: Partial<IEstimateGroupData> = {
            id: newGroupId,
            name: templateGroup.name,
            included: templateGroup.included,
            estimate_id: estimateId,
            sort_order: sortOrder,
            subtotal: 0,
            labor_total: 0,
            product_total: 0,
            discountable_total: 0,
            // New groups inherit estimate-level markup (hand-added-group parity).
            product_markup: null,
            labor_markup: null,
          };

          state = estimateGroupsReducer(state, EstimateGroupActions.setEstimateGroupById(newGroupId, newGroupData));

          const updatedGroupIds = currentGroupIds.push(newGroupId);
          state = state.setIn(["estimates", "byId", estimateId, "group_ids"], updatedGroupIds);

          targetGroupId = newGroupId;
        }

        templateGroup.lineItems.forEach((templateLineItem) => {
          const newLineItemId = estimateLineItemUnsavedIdSelector(state);
          state = estimateLineItemsReducer(state, EstimateLineItemActions.decrementUnsavedId());

          const currentLineItemIds = state.getIn([
            "estimateGroups",
            "byId",
            targetGroupId,
            "line_item_ids",
          ]) as List<number>;
          const lineItemSortOrder = currentLineItemIds.size;
          const targetGroup = estimateGroupSelector(state, { estimateGroupId: targetGroupId });

          // Field source = the resolved product (parity with ADD_ESTIMATE_LINE_ITEM and rsf-app's
          // AddEstimateLineItemCmd → setProduct). The stored template carries only uuids; the async
          // action resolved them to a product id, and the product lives in the hydrated snapshot.
          const lineItemData: any = {
            id: newLineItemId,
            uuid: crypto.randomUUID(),
            estimate_group_id: targetGroupId,
            sort_order: lineItemSortOrder,
            quantity: 1,
            product_price: 0,
            labor_price: 0,
            ext_price: 0,
            labor_ext_price: 0,
            product_ext_price: 0,
            subtotal: 0,
            discountable: "default",
            markupable: "default",
          };

          if (templateLineItem.productId != null) {
            const product = resolveProductForLineItem(state, templateLineItem.productId);
            if (product) {
              Object.assign(lineItemData, productLineItemFields(state, product));
              lineItemData.quantity = deriveLineItemQuantity(state, product, targetGroup, lineItemData.quantity);
            } else {
              lineItemData.product_uuid = templateLineItem.productUuid;
            }
          } else {
            // Custom (non-product) line item — inline fields, no resolution.
            const custom = templateLineItem.custom;
            lineItemData.name = custom?.name ?? "";
            lineItemData.description = custom?.description ?? "";
            lineItemData.base_product_price = custom?.productPrice ?? 0;
            lineItemData.base_labor_price = custom?.laborPrice ?? 0;
            lineItemData.quantity = custom?.quantity ?? 1;
            lineItemData.uom = custom?.uom;
            // product_uuid carries the template's stored identity (rsf-app parity: setCustomProduct
            // sets product_uuid only). The line item's own `uuid` stays the fresh crypto.randomUUID()
            // seeded above — it's the draft→server remap key, so each applied row (even duplicate
            // applies of the same template) must keep a distinct uuid.
            lineItemData.product_uuid = custom?.uuid ?? templateLineItem.productUuid;
          }

          state = estimateLineItemsReducer(
            state,
            EstimateLineItemActions.setEstimateLineItemById(newLineItemId, lineItemData),
          );

          const updatedLineItemIds = currentLineItemIds.push(newLineItemId);
          state = state.setIn(["estimateGroups", "byId", targetGroupId, "line_item_ids"], updatedLineItemIds);

          // Options come from the template's stored item_uuids (resolved to ids by the async action),
          // NOT the product's defaults — replay ADD_ESTIMATE_LINE_ITEM_OPTION so the option's fields
          // are derived from the resolved ProductOption (same source-of-truth as an interactive add).
          (templateLineItem.options ?? []).forEach((option) => {
            state = reducer(
              state,
              EstimateEditorActions.addEstimateLineItemOption(
                newLineItemId,
                option.productOptionId,
                option.productOptionGroupId,
              ),
            );
          });

          state = updateNodeAndParents(state, "lineItem", newLineItemId);
        });

        state = updateNodeAndParents(state, "group", targetGroupId);
      });

      return state;
    }
    case UPDATE_ESTIMATE: {
      const { event } = action.payload;
      state = rsfFormReducer(state, RsfFormActions.update(event));
      return state;
    }
    case ADD_ESTIMATE_GROUP: {
      const { estimateId, estimateGroupData } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot add estimate group: estimate not found", { estimateId });
        return state;
      }

      const newGroupId = estimateGroupUnsavedIdSelector(state);
      state = estimateGroupsReducer(state, EstimateGroupActions.decrementUnsavedId());

      const currentGroupIds = estimateRecord.group_ids;
      const maxSortOrder = currentGroupIds.reduce((max, id) => {
        const so = estimateGroupSelector(state, { estimateGroupId: id })?.sort_order;
        return so != null && so > max ? so : max;
      }, -1);

      const newGroupData: Partial<IEstimateGroupData> = {
        name: "Group",
        included: false,
        estimate_id: estimateId,
        sort_order: maxSortOrder + 1,
        subtotal: 0,
        labor_total: 0,
        product_total: 0,
        discountable_total: 0,
        product_markup: null,
        labor_markup: null,
        ...estimateGroupData,
        id: newGroupId,
      };

      state = estimateGroupsReducer(state, EstimateGroupActions.setEstimateGroupById(newGroupId, newGroupData));

      const updatedGroupIds = currentGroupIds.push(newGroupId);
      state = state.setIn(["estimates", "byId", estimateId, "group_ids"], updatedGroupIds);

      state = renumberEstimateGroupSortOrders(state, estimateId);

      return updateNodeAndParents(state, "group", newGroupId);
    }
    case REMOVE_ESTIMATE_GROUP: {
      const { estimateId, estimateGroupId } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot remove estimate group: estimate not found", { estimateId });
        return state;
      }

      const groupRecord = estimateGroupSelector(state, { estimateGroupId });

      if (estimateGroupId <= 0) {
        // Hard delete: remove from parent and delete the record and all children
        const currentGroupIds = estimateRecord.group_ids;
        const updatedGroupIds = currentGroupIds.filter((id) => id !== estimateGroupId);
        state = state.setIn(["estimates", "byId", estimateId, "group_ids"], updatedGroupIds);

        // Delete all child line items and their options
        if (groupRecord) {
          groupRecord.line_item_ids.forEach((lineItemId) => {
            const lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
            if (lineItemRecord) {
              // Delete all options for this line item
              lineItemRecord.option_ids.forEach((optionId) => {
                state = state.deleteIn(["estimateLineItemOptions", "byId", optionId]);
              });
            }
            state = state.deleteIn(["estimateLineItems", "byId", lineItemId]);
          });
        }

        state = state.deleteIn(["estimateGroups", "byId", estimateGroupId]);
      } else {
        state = state.setIn(["estimateGroups", "byId", estimateGroupId, "_destroy"], true);
      }

      state = renumberEstimateGroupSortOrders(state, estimateId);

      return updateNodeAndParents(state, "subtotals", estimateId);
    }
    case UPDATE_ESTIMATE_GROUP: {
      const { event } = action.payload;
      const { estimateGroupId } = event.editContext;
      state = rsfFormReducer(state, RsfFormActions.update(event));

      // Toggling `included` moves the group between blocks — renumber to keep sort_orders sequential.
      if (event.name === "included") {
        const updatedEstimateId = estimateGroupSelector(state, { estimateGroupId })?.estimate_id;
        if (updatedEstimateId != null) {
          state = renumberEstimateGroupSortOrders(state, updatedEstimateId);
        }
      }

      return updateNodeAndParents(state, "group", estimateGroupId);
    }
    case REORDER_ESTIMATE_GROUP: {
      const { estimateId, oldIndex, newIndex } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot reorder estimate groups: estimate not found", { estimateId });
        return state;
      }

      const currentGroupIds = estimateRecord.group_ids;

      if (oldIndex < 0 || oldIndex >= currentGroupIds.size || newIndex < 0 || newIndex >= currentGroupIds.size) {
        console.error("Invalid indices for reordering estimate groups", {
          oldIndex,
          newIndex,
          size: currentGroupIds.size,
        });
        return state;
      }

      const groupIdToMove = currentGroupIds.get(oldIndex);
      if (groupIdToMove === undefined) {
        return state;
      }

      const withoutOld = currentGroupIds.delete(oldIndex);
      const reorderedGroupIds = withoutOld.insert(newIndex, groupIdToMove);

      state = state.setIn(["estimates", "byId", estimateId, "group_ids"], reorderedGroupIds);

      reorderedGroupIds.forEach((groupId, index) => {
        state = state.setIn(["estimateGroups", "byId", groupId, "sort_order"], index);
      });

      return state;
    }

    case REORDER_GROUPS_IN_BLOCK: {
      const { estimateId, orderedGroupIds } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot reorder groups in block: estimate not found", { estimateId });
        return state;
      }

      // Stamp block-local indices first so the renumber's sort preserves
      // the user's intended order within the dragged block.
      orderedGroupIds.forEach((groupId: number, index: number) => {
        state = state.setIn(["estimateGroups", "byId", groupId, "sort_order"], index);
      });

      return renumberEstimateGroupSortOrders(state, estimateId);
    }

    case ADD_ESTIMATE_LINE_ITEM: {
      const { estimateGroupId, productId } = action.payload;

      const groupRecord = estimateGroupSelector(state, { estimateGroupId });
      if (!groupRecord) {
        console.error("Cannot add line item: estimate group not found", { estimateGroupId });
        return state;
      }

      const newLineItemId = estimateLineItemUnsavedIdSelector(state);
      state = estimateLineItemsReducer(state, EstimateLineItemActions.decrementUnsavedId());

      const currentLineItemIds = groupRecord.line_item_ids;
      const sortOrder = currentLineItemIds.size;

      const lineItemData: any = {
        id: newLineItemId,
        uuid: crypto.randomUUID(),
        estimate_group_id: estimateGroupId,
        sort_order: sortOrder,
        quantity: 1,
        product_price: 0,
        labor_price: 0,
        ext_price: 0,
        labor_ext_price: 0,
        product_ext_price: 0,
        subtotal: 0,
        discountable: "default",
        markupable: "default",
      };

      const product = productId ? resolveProductForLineItem(state, productId) : null;
      if (product) {
        Object.assign(lineItemData, productLineItemFields(state, product));
        lineItemData.quantity = deriveLineItemQuantity(state, product, groupRecord, lineItemData.quantity);
      }

      state = estimateLineItemsReducer(
        state,
        EstimateLineItemActions.setEstimateLineItemById(newLineItemId, lineItemData),
      );

      const updatedLineItemIds = currentLineItemIds.push(newLineItemId);
      state = state.setIn(["estimateGroups", "byId", estimateGroupId, "line_item_ids"], updatedLineItemIds);

      if (product) {
        state = applyDefaultOptions(state, newLineItemId, product);
      }

      return updateNodeAndParents(state, "lineItem", newLineItemId);
    }
    case ADD_CUSTOM_ESTIMATE_LINE_ITEM: {
      const { estimateGroupId, customProductData } = action.payload;

      const groupRecord = estimateGroupSelector(state, { estimateGroupId });
      if (!groupRecord) {
        console.error("Cannot add custom line item: estimate group not found", { estimateGroupId });
        return state;
      }

      const newLineItemId = estimateLineItemUnsavedIdSelector(state);
      state = estimateLineItemsReducer(state, EstimateLineItemActions.decrementUnsavedId());

      const currentLineItemIds = groupRecord.line_item_ids;
      const sortOrder = currentLineItemIds.size;

      const lineItemData: any = {
        id: newLineItemId,
        uuid: crypto.randomUUID(),
        estimate_group_id: estimateGroupId,
        sort_order: sortOrder,
        quantity: customProductData.quantity || 1,
        base_product_price: customProductData.product_price || 0,
        base_labor_price: customProductData.labor_price || 0,
        ext_price: 0,
        labor_ext_price: 0,
        product_ext_price: 0,
        subtotal: 0,
        discountable: "default",
        markupable: "default",
        name: customProductData.name || "",
        description: customProductData.description || "",
        product_uuid: customProductData.uuid,
        uom: customProductData.uom,
      };

      state = estimateLineItemsReducer(
        state,
        EstimateLineItemActions.setEstimateLineItemById(newLineItemId, lineItemData),
      );

      const updatedLineItemIds = currentLineItemIds.push(newLineItemId);
      state = state.setIn(["estimateGroups", "byId", estimateGroupId, "line_item_ids"], updatedLineItemIds);

      return updateNodeAndParents(state, "lineItem", newLineItemId);
    }
    case SET_ESTIMATE_LINE_ITEM_PRODUCT: {
      const { lineItemId, productId, options } = action.payload;
      const keepDescription = options?.keepDescription ?? false;
      const keepQuantity = options?.keepQuantity ?? false;

      const product = resolveProductForLineItem(state, productId);
      if (!product) {
        console.error("Cannot set product on line item: product not found", { productId });
        return state;
      }

      const existingLineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      const group = existingLineItem
        ? estimateGroupSelector(state, { estimateGroupId: existingLineItem.estimate_group_id })
        : null;

      const fields = productLineItemFields(state, product);
      // A variant swap can opt to keep a user-edited description (don't overwrite it with the new
      // product's description) and/or a user-overridden quantity (don't recompute from the link).
      if (keepDescription) {
        delete fields.description;
      }
      fields.quantity = keepQuantity
        ? (existingLineItem?.quantity ?? 1)
        : deriveLineItemQuantity(state, product, group, existingLineItem?.quantity ?? 1);

      state = state.mergeIn(["estimateLineItems", "byId", lineItemId], fields);

      state = applyDefaultOptions(state, lineItemId, product);

      return updateNodeAndParents(state, "lineItem", lineItemId);
    }
    case REMOVE_ESTIMATE_LINE_ITEM: {
      const { estimateGroupId, lineItemId } = action.payload;

      const groupRecord = estimateGroupSelector(state, { estimateGroupId });
      if (!groupRecord) {
        console.error("Cannot remove line item: estimate group not found", { estimateGroupId });
        return state;
      }

      const lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });

      if (lineItemId <= 0) {
        // Hard delete: remove from parent and delete the record and all child options
        const currentLineItemIds = groupRecord.line_item_ids;
        const updatedLineItemIds = currentLineItemIds.filter((id) => id !== lineItemId);
        state = state.setIn(["estimateGroups", "byId", estimateGroupId, "line_item_ids"], updatedLineItemIds);

        // Delete all child options
        if (lineItemRecord) {
          lineItemRecord.option_ids.forEach((optionId) => {
            state = state.deleteIn(["estimateLineItemOptions", "byId", optionId]);
          });
        }

        state = state.deleteIn(["estimateLineItems", "byId", lineItemId]);
      } else {
        state = state.setIn(["estimateLineItems", "byId", lineItemId, "_destroy"], true);
      }

      return updateNodeAndParents(state, "group", estimateGroupId);
    }
    case UPDATE_ESTIMATE_LINE_ITEM: {
      const { event } = action.payload;
      const { lineItemId } = event.editContext;
      state = rsfFormReducer(state, RsfFormActions.update(event));

      return updateNodeAndParents(state, "lineItem", lineItemId);
    }
    case REORDER_ESTIMATE_LINE_ITEM: {
      const { estimateGroupId, oldIndex, newIndex } = action.payload;

      const groupRecord = estimateGroupSelector(state, { estimateGroupId });
      if (!groupRecord) {
        console.error("Cannot reorder line items: estimate group not found", { estimateGroupId });
        return state;
      }

      const currentLineItemIds = groupRecord.line_item_ids;

      if (oldIndex < 0 || oldIndex >= currentLineItemIds.size || newIndex < 0 || newIndex >= currentLineItemIds.size) {
        console.error("Invalid indices for reordering line items", {
          oldIndex,
          newIndex,
          size: currentLineItemIds.size,
        });
        return state;
      }

      const lineItemIdToMove = currentLineItemIds.get(oldIndex);
      if (lineItemIdToMove === undefined) {
        return state;
      }

      const withoutOld = currentLineItemIds.delete(oldIndex);
      const reorderedLineItemIds = withoutOld.insert(newIndex, lineItemIdToMove);

      state = state.setIn(["estimateGroups", "byId", estimateGroupId, "line_item_ids"], reorderedLineItemIds);

      reorderedLineItemIds.forEach((lineItemId, index) => {
        state = state.setIn(["estimateLineItems", "byId", lineItemId, "sort_order"], index);
      });

      return state;
    }
    case MOVE_ESTIMATE_LINE_ITEM: {
      const { oldEstimateGroupId, newEstimateGroupId, oldIndex, newIndex } = action.payload;

      const oldGroupRecord = estimateGroupSelector(state, { estimateGroupId: oldEstimateGroupId });
      if (!oldGroupRecord) {
        console.error("Cannot move line item: old estimate group not found", { oldEstimateGroupId });
        return state;
      }

      const newGroupRecord = estimateGroupSelector(state, { estimateGroupId: newEstimateGroupId });
      if (!newGroupRecord) {
        console.error("Cannot move line item: new estimate group not found", { newEstimateGroupId });
        return state;
      }

      const oldLineItemIds = oldGroupRecord.line_item_ids;
      const lineItemIdToMove = oldLineItemIds.get(oldIndex);
      if (lineItemIdToMove === undefined) {
        console.error("Cannot move line item: invalid old index", { oldIndex, size: oldLineItemIds.size });
        return state;
      }

      const updatedOldLineItemIds = oldLineItemIds.delete(oldIndex);
      state = state.setIn(["estimateGroups", "byId", oldEstimateGroupId, "line_item_ids"], updatedOldLineItemIds);

      const newLineItemIds = newGroupRecord.line_item_ids;
      const updatedNewLineItemIds = newLineItemIds.insert(newIndex, lineItemIdToMove);
      state = state.setIn(["estimateGroups", "byId", newEstimateGroupId, "line_item_ids"], updatedNewLineItemIds);

      state = state.setIn(["estimateLineItems", "byId", lineItemIdToMove, "estimate_group_id"], newEstimateGroupId);
      state = updateNodeAndParents(state, "lineItem", lineItemIdToMove);

      updatedOldLineItemIds.forEach((lineItemId, index) => {
        state = state.setIn(["estimateLineItems", "byId", lineItemId, "sort_order"], index);
      });
      updatedNewLineItemIds.forEach((lineItemId, index) => {
        state = state.setIn(["estimateLineItems", "byId", lineItemId, "sort_order"], index);
      });

      state = updateNodeAndParents(state, "group", oldEstimateGroupId);
      return updateNodeAndParents(state, "group", newEstimateGroupId);
    }
    case UPDATE_ESTIMATE_LINE_ITEM_DEFAULTS: {
      const { estimateId, lineItemId } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot update line item defaults: estimate not found", { estimateId });
        return state;
      }

      const lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (!lineItemRecord) {
        console.error("Cannot update line item defaults: line item not found", { lineItemId });
        return state;
      }

      // Key by the selected OPTION's `match_id`, value carries the GROUP's `match_id` (stored in the
      // `option_group_uuid` field) + quantity — rsf-app `setDefaultOptions`. `match_id` (not `uuid`,
      // which differs per product) is what lets Use resolve the same option on another product.
      let newOptions = Map<string, DefaultOptionRecord>();

      lineItemRecord.option_ids.forEach((optionId) => {
        const optionRecord = estimateLineItemOptionSelector(state, { estimateLineItemOptionId: optionId });
        if (!optionRecord || optionRecord._destroy) return;

        const optionMatchId = optionRecord.product_option_match_id;
        if (!optionMatchId) return;

        const productOptionGroup = productOptionGroupById(state, optionRecord.product_option_group_id);
        if (!productOptionGroup || !productOptionGroup.match_id) return;

        const defaultOption = defaultOptionFromJSON({
          option_group_uuid: productOptionGroup.match_id,
          quantity: optionRecord.quantity,
        });

        newOptions = newOptions.set(optionMatchId, defaultOption);
      });

      const newDefaults = defaultsFromJSON({ options: newOptions.toJS() as any });
      return state.setIn(["estimates", "byId", estimateId, "defaults"], newDefaults);
    }
    case APPLY_ESTIMATE_LINE_ITEM_DEFAULTS: {
      const { estimateId, lineItemId } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot apply line item defaults: estimate not found", { estimateId });
        return state;
      }

      const lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (!lineItemRecord) {
        console.error("Cannot apply line item defaults: line item not found", { lineItemId });
        return state;
      }

      const defaults = estimateRecord.defaults;
      if (!defaults || defaults.options.size === 0) {
        return state;
      }

      const product = resolveProductForLineItem(state, lineItemRecord.product_id);
      if (!product) {
        console.error("Cannot apply line item defaults: product not found", {
          productId: lineItemRecord.product_id,
        });
        return state;
      }

      // Resolve each saved default (keyed by option match_id, carrying its group's match_id) to this
      // line item's product option in the activated-price-list snapshot, then clear the current
      // options and add the resolved ones (rsf-app `updateLineItemOptions` → `addEstimateDefaultOptions`).
      const optionsToAdd: DefaultOptionToAdd[] = [];
      defaults.options.forEach((defaultOption, optionMatchId) => {
        const productOption = productOptionByMatchId(state, {
          product,
          productOptionGroupMatchId: defaultOption.option_group_uuid,
          productOptionMatchId: optionMatchId,
        });
        if (!productOption) return;

        const group = productOptionGroupById(state, productOption.option_group_id);
        optionsToAdd.push({
          productOptionId: productOption.id,
          productOptionGroupId: group?.id ?? productOption.option_group_id,
          quantity: defaultOption.quantity,
        });
      });

      state = applyDefaultOptionsFromList(state, lineItemId, optionsToAdd, reducer);

      return updateNodeAndParents(state, "lineItem", lineItemId);
    }

    case SET_LINE_ITEM_OPENING_ESTIMATIONS: {
      const { lineItemId, openings } = action.payload;

      const lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (!lineItemRecord) {
        console.error("Cannot set line item opening estimations: line item not found", { lineItemId });
        return state;
      }

      const isSelected = (oe: OpeningEstimationRecord) =>
        openings.some((o) => o.kind === oe.openable_type && o.id === oe.openable_id);

      // Reconcile against the normalized slice (rsf-app updateOpeningsSelection): undelete matched
      // rows, soft-delete deselected persisted rows (kept on `opening_estimation_ids` so the save
      // payload carries `_destroy`), drop deselected unsaved rows. Mirrors the estimateLineItemOptions
      // write path (soft-delete persisted / delete unsaved / mint new via decrementUnsavedId).
      const originalIds = lineItemRecord.opening_estimation_ids ?? List<number>();
      let openingEstimationIds = originalIds;
      originalIds.forEach((oeId) => {
        const oe = openingEstimationById(state, oeId);
        if (!oe) return;
        if (isSelected(oe)) {
          state = state.setIn(["openingEstimations", "byId", oeId, "_destroy"], null);
        } else if (oe.id > 0) {
          state = state.setIn(["openingEstimations", "byId", oeId, "_destroy"], true);
        } else {
          state = state.deleteIn(["openingEstimations", "byId", oeId]);
          openingEstimationIds = openingEstimationIds.filter((id) => id !== oeId);
        }
      });

      openings.forEach((opening) => {
        const kind = opening.kind;
        if (kind !== "Window" && kind !== "Door") return;
        const exists = openingEstimationIds.some((oeId) => {
          const oe = openingEstimationById(state, oeId);
          return Boolean(oe && oe.openable_id === opening.id && oe.openable_type === kind);
        });
        if (!exists) {
          const newOeId = openingEstimationUnsavedId(state);
          state = openingEstimationReducer(state, OpeningEstimationActions.decrementUnsavedId());
          state = state.setIn(
            ["openingEstimations", "byId", newOeId],
            new OpeningEstimationRecord({
              id: newOeId,
              openable_type: kind,
              openable_id: opening.id,
              estimate_line_item_id: lineItemId,
              openable: opening,
            }),
          );
          openingEstimationIds = openingEstimationIds.push(newOeId);
        }
      });

      state = state.setIn(["estimateLineItems", "byId", lineItemId, "opening_estimation_ids"], openingEstimationIds);

      return updateNodeAndParents(state, "lineItem", lineItemId);
    }
    case APPLY_WINDOW_PRICE_ADJUSTMENT: {
      const { lineItemId, productId } = action.payload;

      const lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (!lineItemRecord) {
        console.error("Cannot apply window price adjustment: line item not found", { lineItemId });
        return state;
      }
      const product = resolveProductForLineItem(state, productId);
      if (!product) {
        console.error("Cannot apply window price adjustment: product not found", { productId });
        return state;
      }

      // Resolve the first live opening at use — persisted estimations resolve from the
      // windows/doors store; unsaved attachments fall back to the client-side `openable`. The live
      // (non-destroyed) estimations are resolved from the normalized slice by the line item's
      // `opening_estimation_ids`. Resolved inline (rather than via a selector import) to avoid a
      // module-init cycle through measurement.common.selectors.
      const firstOe = (lineItemRecord.opening_estimation_ids ?? List<number>())
        .map((oeId) => openingEstimationById(state, oeId))
        .find((oe): oe is OpeningEstimationRecord => Boolean(oe) && !oe._destroy);
      let opening: OpeningRecord | null = firstOe?.openable ?? null;
      if (firstOe?.openable_type && firstOe.openable_id) {
        opening =
          (state.getIn([`${firstOe.openable_type.toLowerCase()}s`, "byId", firstOe.openable_id]) as
            | OpeningRecord
            | undefined) ??
          firstOe.openable ??
          null;
      }
      const adjustment = windowPriceAdjustment(lineItemRecord, product, opening);
      if (!adjustment) {
        return state;
      }

      state = state.mergeIn(["estimateLineItems", "byId", lineItemId], adjustment);
      return updateNodeAndParents(state, "lineItem", lineItemId);
    }
    case CHANGE_LINE_ITEM_PRODUCT_WITH_OPTIONS: {
      const { lineItemId, productId, matchedOptions } = action.payload;

      const product = resolveProductForLineItem(state, productId);
      if (!product) {
        console.error("Cannot change line item product: product not found", { productId });
        return state;
      }

      const existingLineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (!existingLineItem) {
        console.error("Cannot change line item product: line item not found", { lineItemId });
        return state;
      }

      const group = estimateGroupSelector(state, { estimateGroupId: existingLineItem.estimate_group_id });

      // Copy the new product's fields (rsf-app setProduct) but do NOT seed the product's default
      // options — the wizard carries over the compareOptions-matched selection instead.
      const fields = productLineItemFields(state, product) as Record<string, unknown>;
      fields.quantity = deriveLineItemQuantity(state, product, group, existingLineItem.quantity);
      state = state.mergeIn(["estimateLineItems", "byId", lineItemId], fields);

      // Clear the current options: soft-destroy persisted rows (kept on option_ids so the save
      // serializer emits `_destroy`), hard-delete unsaved ones (rsf-app confirmedChangeProduct's
      // unselectOption over existingOptions).
      const refreshed = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (refreshed && refreshed.option_ids.size > 0) {
        const persistedOptionIds = refreshed.option_ids.filter((id) => id > 0);
        refreshed.option_ids
          .filter((id) => id <= 0)
          .forEach((id) => {
            state = state.deleteIn(["estimateLineItemOptions", "byId", id]);
          });
        persistedOptionIds.forEach((id) => {
          state = state.setIn(["estimateLineItemOptions", "byId", id, "_destroy"], true);
        });
        state = state.setIn(["estimateLineItems", "byId", lineItemId, "option_ids"], persistedOptionIds);
      }

      // Re-add the matched options with their preserved quantities.
      matchedOptions.forEach(({ productOptionId, productOptionGroupId, quantity }) => {
        state = reducer(
          state,
          EstimateEditorActions.addEstimateLineItemOption(lineItemId, productOptionId, productOptionGroupId, quantity),
        );
      });

      return updateNodeAndParents(state, "lineItem", lineItemId);
    }
    case ADD_ESTIMATE_LINE_ITEM_OPTION: {
      const { lineItemId, productOptionId, productOptionGroupId, quantity = 1 } = action.payload;

      let lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (!lineItemRecord) {
        console.error("Cannot add line item option: line item not found", { lineItemId });
        return state;
      }

      // Resolve the option from the global productOptions store (the snapshot's options are fanned
      // into it on load, alongside options loaded individually e.g. by the option-group editor).
      const productOption = productOptionId ? productOptionById(state, productOptionId) : null;
      // Prefer the caller-supplied group (the snapshot group the link points at — rsf-app parity);
      // the option's own option_group_id can be a stale source id absent from the snapshot store.
      const optionGroup = productOption
        ? productOptionGroupById(state, productOptionGroupId ?? productOption.option_group_id)
        : null;

      // Single-select replacement (unconditional, no opt-in param): a single-select group holds at most
      // one option, so soft-remove all prior non-destroyed options in this group before adding the new
      // one. rsf-app applies this only on the manual path; doing it in the reducer collapses a misconfigured
      // default/template that seeded two options into a single-select group to one — a deliberate, more-
      // correct micro-divergence covered by a reducer test.
      if (optionGroup && optionGroup.selection_mode === "single") {
        const existingOptionIds = lineItemRecord.option_ids.filter((id) => {
          const existing = estimateLineItemOptionSelector(state, { estimateLineItemOptionId: id });
          return Boolean(existing && !existing._destroy && existing.product_option_group_id === optionGroup.id);
        });
        if (existingOptionIds.size > 0) {
          existingOptionIds.forEach((existingOptionId) => {
            if (existingOptionId <= 0) {
              const remainingOptionIds = (
                state.getIn(["estimateLineItems", "byId", lineItemId, "option_ids"]) as List<number>
              ).filter((id) => id !== existingOptionId);
              state = state.setIn(["estimateLineItems", "byId", lineItemId, "option_ids"], remainingOptionIds);
              state = state.deleteIn(["estimateLineItemOptions", "byId", existingOptionId]);
            } else {
              state = state.setIn(["estimateLineItemOptions", "byId", existingOptionId, "_destroy"], true);
            }
          });
          // option_ids may have changed — re-read the line item before pushing the new option.
          lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
        }
      }

      const newOptionId = estimateLineItemOptionUnsavedIdSelector(state);
      state = estimateLineItemOptionsReducer(state, EstimateLineItemOptionActions.decrementUnsavedId());

      const optionData: any = {
        id: newOptionId,
        estimate_line_item_id: lineItemId,
        quantity,
        product_price: 0,
        labor_price: 0,
        ext_price: 0,
        labor_ext_price: 0,
        product_ext_price: 0,
        subtotal: 0,
      };

      if (productOption) {
        optionData.product_option_id = productOptionId;
        optionData.product_option_uuid = productOption.uuid;
        optionData.product_option_match_id = productOption.match_id;
        optionData.name = productOption.name;
        optionData.description = productOption.description;
        optionData.base_product_price = productOption.product_price || 0;
        optionData.base_labor_price = productOption.labor_price || 0;
        optionData.uom = productOption.uom;
        optionData.charge_type = productOption.charge_type;
        optionData.pog_type = productOption.pog_type;
        optionData.cost = productOption.cost;
        optionData.msrp = productOption.msrp;
        optionData.price = productOption.price;
        optionData.product_option_image_ids = productOption.image_ids;
        // Keep the ids — rsf-mobile reads product_option_image_ids[0].
        optionData.product_option_image_urls = imageUrlsFromIds(state, productOption.image_ids);

        // For percent uom, use prices as factors
        if (productOption.uom === "percent") {
          optionData.labor_factor = productOption.labor_price;
          optionData.product_factor = productOption.product_price;
        }

        if (optionGroup) {
          optionData.product_option_group_id = optionGroup.id;
          optionData.product_option_group_name = optionGroup.name;
        } else {
          optionData.product_option_group_id = productOption.option_group_id;
        }
      }

      state = estimateLineItemOptionsReducer(
        state,
        EstimateLineItemOptionActions.setEstimateLineItemOptionById(newOptionId, optionData),
      );

      const currentOptionIds = lineItemRecord.option_ids;
      const updatedOptionIds = currentOptionIds.push(newOptionId);
      state = state.setIn(["estimateLineItems", "byId", lineItemId, "option_ids"], updatedOptionIds);

      return updateNodeAndParents(state, "option", newOptionId);
    }
    case REMOVE_ESTIMATE_LINE_ITEM_OPTION: {
      const { lineItemId, optionId } = action.payload;

      const lineItemRecord = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (!lineItemRecord) {
        console.error("Cannot remove line item option: line item not found", { lineItemId });
        return state;
      }

      if (optionId <= 0) {
        const currentOptionIds = lineItemRecord.option_ids;
        const updatedOptionIds = currentOptionIds.filter((id) => id !== optionId);
        state = state.setIn(["estimateLineItems", "byId", lineItemId, "option_ids"], updatedOptionIds);
        state = state.deleteIn(["estimateLineItemOptions", "byId", optionId]);
      } else {
        state = state.setIn(["estimateLineItemOptions", "byId", optionId, "_destroy"], true);
      }

      return updateNodeAndParents(state, "lineItem", lineItemId);
    }
    case UPDATE_ESTIMATE_LINE_ITEM_OPTION: {
      const { event } = action.payload;
      const { estimateLineItemOptionId } = event.editContext;
      state = rsfFormReducer(state, RsfFormActions.update(event));

      return updateNodeAndParents(state, "option", estimateLineItemOptionId);
    }

    case ADD_ESTIMATE_DISCOUNT: {
      const { estimateId, discountId } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot add discount: estimate not found", { estimateId });
        return state;
      }

      const discount = discountId ? discountSelector(state, { discountId }) : null;

      const estimateDiscount = estimateDiscountFromJSON({
        kind: discount?.breakdown.kind,
        name: discount?.name,
        amount: discount?.breakdown.amount || 0,
        limit: discount?.breakdown.limit || 0,
        discountId: discountId ?? null,
      });

      const updatedDiscounts = estimateRecord.discounts.push(estimateDiscount);
      state = state.setIn(["estimates", "byId", estimateId, "discounts"], updatedDiscounts);

      return updateNodeAndParents(state, "discountTotals", estimateId);
    }
    case REMOVE_ESTIMATE_DISCOUNT: {
      const { estimateId, estimateDiscountIndex } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot remove discount: estimate not found", { estimateId });
        return state;
      }

      const discounts = estimateRecord.discounts;
      if (estimateDiscountIndex < 0 || estimateDiscountIndex >= discounts.size) {
        console.error("Invalid discount index", { estimateDiscountIndex, size: discounts.size });
        return state;
      }

      const updatedDiscounts = discounts.delete(estimateDiscountIndex);
      state = state.setIn(["estimates", "byId", estimateId, "discounts"], updatedDiscounts);

      return updateNodeAndParents(state, "discountTotals", estimateId);
    }
    case UPDATE_ESTIMATE_DISCOUNT: {
      const { event } = action.payload;
      const { estimateId } = event.editContext;
      state = rsfFormReducer(state, RsfFormActions.update(event));

      return updateNodeAndParents(state, "discountTotals", estimateId);
    }

    case UPDATE_SALES_TAX: {
      const { event } = action.payload;
      const { estimateId } = event.editContext;
      // Capture the pre-update tax type so we can tell a real switch into None from a re-select of the
      // already-active None: react-select fires onChange even when the current option is re-clicked, and
      // rsfFormReducer is about to overwrite the value below.
      const wasNone = state.getIn(["estimates", "byId", estimateId, "default_taxable_amount"]) === "none";
      state = rsfFormReducer(state, RsfFormActions.update(event));
      // Selecting Tax Type "None" zeroes the taxable base on the switch (BR-2, RSF-25852). calcTaxTotals'
      // none branch keeps the prior taxable_amount, so without this the stale base — and its tax — would
      // linger. Zeroing only on the switch into None — not on a re-select of None, every recalc, or load
      // — leaves the field editable for manual re-entry (a re-select must not wipe a manually entered
      // base) and leaves saved None estimates untouched on reload.
      if (event.name === "default_taxable_amount" && event.value === "none" && !wasNone) {
        state = state.setIn(["estimates", "byId", estimateId, "taxable_amount"], 0);
      }
      return updateNodeAndParents(state, "taxTotals", estimateId);
    }

    case ADD_ESTIMATE_PAYMENT_TERM: {
      const { estimateId, paymentTermId } = action.payload;

      const paymentTermRecord = paymentTerm(state, { paymentTermId });
      if (!paymentTermRecord) {
        console.error("Cannot add payment term: payment term not found", { paymentTermId });
        return state;
      }

      const paymentTermsData: Partial<IEstimatePaymentTermsData> = {
        id: paymentTermRecord.id,
        name: paymentTermRecord.name,
        description: paymentTermRecord.description,
        financed_amount: 0,
        payment_term_items: paymentTermRecord.breakdown.items
          .map((item) => ({
            uuid: item.uuid,
            amount: item.amount,
            description: item.description,
            type: item.type,
            calculated: 0,
            paymentMethod: item.paymentMethod,
          }))
          .toArray() as IEstimatePaymentTermItemData[],
      };

      const newPaymentTerms = estimatePaymentTermsFromJSON(paymentTermsData);
      state = state.setIn(["estimates", "byId", estimateId, "payment_terms"], newPaymentTerms);

      return updateNodeAndParents(state, "paymentTermsTotals", estimateId);
    }

    case ADD_ESTIMATE_PAYMENT_TERM_ITEM: {
      const { estimateId } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot add payment term item: estimate not found", { estimateId });
        return state;
      }

      const newPaymentTermItem = estimatePaymentTermItemFromJSON({
        amount: 0,
        description: "",
        type: "percent",
        calculated: 0,
        paymentMethod: "cash",
      });

      const currentItems = estimateRecord.payment_terms.payment_term_items;
      const updatedItems = currentItems.push(newPaymentTermItem);
      state = state.setIn(["estimates", "byId", estimateId, "payment_terms", "payment_term_items"], updatedItems);

      return updateNodeAndParents(state, "paymentTermsTotals", estimateId);
    }
    case REMOVE_ESTIMATE_PAYMENT_TERM_ITEM: {
      const { estimateId, paymentTermItemUuid } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot remove payment term item: estimate not found", { estimateId });
        return state;
      }

      const currentItems = estimateRecord.payment_terms.payment_term_items;
      const paymentTermItemIndex = currentItems.findIndex((item) => item.uuid === paymentTermItemUuid);
      if (paymentTermItemIndex < 0) {
        console.error("Cannot remove payment term item: uuid not found", { paymentTermItemUuid });
        return state;
      }

      const updatedItems = currentItems.delete(paymentTermItemIndex);
      state = state.setIn(["estimates", "byId", estimateId, "payment_terms", "payment_term_items"], updatedItems);

      return updateNodeAndParents(state, "paymentTermsTotals", estimateId);
    }
    case UPDATE_ESTIMATE_PAYMENT_TERM_ITEM: {
      const { event } = action.payload;
      const { estimateId } = event.editContext;
      state = rsfFormReducer(state, RsfFormActions.update(event));

      return updateNodeAndParents(state, "paymentTermsTotals", estimateId);
    }

    case ADD_ESTIMATE_FINANCE_OPTION: {
      const { estimateId, financeOptionId } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot add finance option: estimate not found", { estimateId });
        return state;
      }

      const financeOptionRecord = financeOption(state, { financeOptionId });
      if (!financeOptionRecord) {
        console.error("Cannot add finance option: finance option not found", { financeOptionId });
        return state;
      }

      // Get the next unsaved ID before creating the record
      const newEstimateFinanceOptionId = estimateFinanceOptionUnsavedIdSelector(state);

      // Create the estimate finance option record in the new store
      state = estimateFinanceOptionReducer(
        state,
        EstimateFinanceOptionActions.addEstimateFinanceOption(estimateId, financeOptionId),
      );

      // Add the new estimate finance option ID to the estimate's finance_option_ids
      const currentFinanceOptionIds = estimateRecord.finance_option_ids;
      if (!currentFinanceOptionIds.includes(newEstimateFinanceOptionId)) {
        const updatedFinanceOptionIds = currentFinanceOptionIds.push(newEstimateFinanceOptionId);
        state = state.setIn(["estimates", "byId", estimateId, "finance_option_ids"], updatedFinanceOptionIds);
      }

      return updateNodeAndParents(state, "financeOptionPayments", estimateId);
    }
    case REMOVE_ESTIMATE_FINANCE_OPTION: {
      const { estimateId, estimateFinanceOptionId } = action.payload;

      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot remove finance option: estimate not found", { estimateId });
        return state;
      }

      if (estimateFinanceOptionId < 0) {
        state = state.deleteIn(["estimateFinanceOptions", "byId", estimateFinanceOptionId]);
        const remainingFinanceOptionIds = estimateRecord.finance_option_ids.filter(
          (id: number) => id !== estimateFinanceOptionId,
        );
        state = state.setIn(["estimates", "byId", estimateId, "finance_option_ids"], remainingFinanceOptionIds);
      } else {
        state = estimateFinanceOptionReducer(
          state,
          EstimateFinanceOptionActions.removeEstimateFinanceOption(estimateFinanceOptionId),
        );
      }

      return updateNodeAndParents(state, "financeOptionPayments", estimateId);
    }
    case START_ADDING_LINE_ITEM: {
      const { estimateId, estimateGroupId } = action.payload;

      // The editing snapshot is scoped to the edited line item's subtree; a brand-new draft owns
      // nothing yet, so there is no estimate/group/sibling tree to copy. The fresh draft's own snapshot
      // (stored below) drives dirty detection, and newDraftId marks it as a create session so Cancel
      // removes it (rather than restoring it) and the editor title + Delete / product-selector gating
      // can tell a new draft from a committed one.
      const estimate = estimateSelector(state, { estimateId });
      if (!estimate) {
        console.error("Cannot start adding line item: estimate not found", { estimateId });
        return state;
      }

      // Get unsaved ID for the new line item
      const lineItemId = estimateLineItemUnsavedIdSelector(state);

      // Decrement unsaved ID
      state = estimateLineItemsReducer(state, EstimateLineItemActions.decrementUnsavedId());

      // Determine sort order (add to end of group's line items)
      const targetGroup = estimateGroupSelector(state, { estimateGroupId });
      const currentLineItemIds = targetGroup ? targetGroup.line_item_ids : List<number>();
      const lineItemSortOrder = currentLineItemIds.size;

      // Create draft line item data (cast to any since it's a partial object)
      const lineItemData: any = {
        id: lineItemId,
        uuid: crypto.randomUUID(),
        estimate_group_id: estimateGroupId,
        sort_order: lineItemSortOrder,
        product_id: null,
        product_uuid: "",
        name: "",
        description: "",
        quantity: 1,
        uom: "ea",
        price: 0,
        ext_price: 0,
        base_product_price: 0,
        base_labor_price: 0,
        product_price: 0,
        labor_price: 0,
        labor_ext_price: 0,
        product_ext_price: 0,
        subtotal: 0,
        cost: 0,
        msrp: 0,
        discountable: "yes",
        markupable: "yes",
        visibility: "always",
        _destroy: false,
      };

      // Add line item to redux
      state = estimateLineItemsReducer(
        state,
        EstimateLineItemActions.setEstimateLineItemById(lineItemId, lineItemData),
      );

      // Store the initial draft as a snapshot so isLineItemDirty can detect when
      // the user has modified a new item (enabling the cancel warning)
      const freshDraft = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (freshDraft) {
        state = state.setIn(["estimateLineItems", "editingSnapshotById", lineItemId], freshDraft);
      }

      // Mark this session as a brand-new draft (Cancel removes it; drives isNewDraftLineItem).
      state = state.setIn(["estimateLineItems", "newDraftId"], lineItemId);

      // Add line item ID to group
      const groupWithLineItem = estimateGroupSelector(state, { estimateGroupId });
      if (groupWithLineItem) {
        const updatedLineItemIds = groupWithLineItem.line_item_ids.push(lineItemId);
        state = state.setIn(["estimateGroups", "byId", estimateGroupId, "line_item_ids"], updatedLineItemIds);
      }

      return state;
    }
    case START_LINE_ITEM_SESSION: {
      const { lineItemId } = action.payload;

      const snapshot = state.getIn(["estimateLineItems", "editingSnapshotById"]) as Map<number, any>;

      // The loader and the component can both fire this for a single edit; once this id is snapshotted
      // the second call must not re-capture a mid-edit state. The check is per-id (not "is any snapshot
      // present") so starting a session for a different line item re-targets rather than being rejected.
      if (lineItemId != null && snapshot.has(lineItemId)) {
        return state;
      }

      // Re-targeting to a different line item: drop any stale snapshot from a prior session before
      // capturing the new one. The editor-switch UI guard has already committed or cancelled that prior
      // session, so this only clears leftover snapshot maps — it never reverts or deletes a row.
      if (snapshot.size > 0) {
        state = state.setIn(["estimateLineItems", "editingSnapshotById"], Map());
        state = state.setIn(["estimateLineItemOptions", "editingSnapshotById"], Map());
        state = state.setIn(["openingEstimations", "editingSnapshotById"], Map());
        state = state.setIn(["estimateLineItems", "newDraftId"], null);
      }

      // Snapshot ONLY the edited line item's owned subtree (record + options + opening-estimations) for
      // rollback. The estimate/group/sibling records are not copied — their only rollback-relevant data
      // was materialized totals, which are deterministically recomputed on cancel. No lineItemId (the
      // wizard create path) opens an empty session: the draft it then mints is marked explicitly and
      // removed, not restored, on cancel.
      if (lineItemId == null) {
        return state;
      }

      const lineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
      if (!lineItem || lineItem._destroy) {
        return state;
      }
      state = state.setIn(["estimateLineItems", "editingSnapshotById", lineItemId], lineItem);

      lineItem.option_ids.forEach((optionId) => {
        const option = estimateLineItemOptionSelector(state, { estimateLineItemOptionId: optionId });
        if (option && !option._destroy) {
          state = state.setIn(["estimateLineItemOptions", "editingSnapshotById", optionId], option);
        }
      });

      lineItem.opening_estimation_ids.forEach((oeId) => {
        const oe = openingEstimationById(state, oeId);
        if (oe && !oe._destroy) {
          state = state.setIn(["openingEstimations", "editingSnapshotById", oeId], oe);
        }
      });

      return state;
    }
    case MARK_LINE_ITEM_DRAFT: {
      const { lineItemId } = action.payload;
      return state.setIn(["estimateLineItems", "newDraftId"], lineItemId);
    }
    case CANCEL_LINE_ITEM_SESSION: {
      // Roll back a single line item's editing session. The snapshot is scoped to the edited item's own
      // subtree, so Cancel restores ONLY that item — a saved sibling the rep deleted during the session
      // was never snapshotted and therefore stays deleted. Totals are NOT recomputed here: the
      // cancelLineItemSession thunk runs afterEstimateMutation (recalc + revalidate) after this reducer,
      // so cancel goes through the same chokepoint as every other mutation (and validations can't go
      // stale). COMMIT keeps its own in-reducer recompute for now.
      const { lineItemId: sessionLineItemId } = action.payload;

      const snapshotLineItems = state.getIn(["estimateLineItems", "editingSnapshotById"]) as Map<number, any>;
      const snapshotOptions = state.getIn(["estimateLineItemOptions", "editingSnapshotById"]) as Map<number, any>;
      const snapshotOpeningEstimations = state.getIn(["openingEstimations", "editingSnapshotById"]) as Map<
        number,
        OpeningEstimationRecord
      >;
      const newDraftId = state.getIn(["estimateLineItems", "newDraftId"]) as number | null;

      // Nothing to roll back when no line item was snapshotted and no draft is marked: deleting a
      // persisted row on cancel must be structurally impossible. Without this guard a cancel dispatched
      // against an empty session (e.g. after an editor switch already cleared the snapshot) would treat
      // the passed id as an unsaved draft and delete a saved row.
      if (snapshotLineItems.size === 0 && newDraftId == null) {
        return state;
      }

      // Drafts to REMOVE rather than restore: only the explicitly-marked new draft (newDraftId) — the
      // "Add Line Item" draft, which sits in the snapshot for dirty detection, and the Window Estimator
      // create draft, which is marked in addWizardProducts. Draft-ness is never inferred from snapshot
      // absence; everything else in the snapshot is an edited existing item to restore.
      const draftIds = new Set<number>();
      if (newDraftId != null) draftIds.add(newDraftId);

      // Every line item this session touched: snapshot entries (restored items + the add draft) plus
      // the passed session item (covers a not-snapshotted wizard draft). Used to reconcile
      // options/opening-estimations/files and to recompute the right groups.
      const sessionLineItemIds = new Set<number>(snapshotLineItems.keySeq().toArray());
      if (sessionLineItemId != null) sessionLineItemIds.add(sessionLineItemId);

      // Collect current (pre-restore) option/opening-estimation ids and negative-id file uploads for
      // the affected items so they can be reconciled against the snapshot below.
      const currentOptionIdsInSession = new Set<number>();
      const currentOpeningEstimationIdsInSession = new Set<number>();
      const preRestoreNegDocIds = new Set<number>();
      const preRestoreNegImgIds = new Set<number>();

      sessionLineItemIds.forEach((id) => {
        const current = estimateLineItemSelector(state, { estimateLineItemId: id });
        if (!current) return;
        current.option_ids.forEach((optionId: number) => currentOptionIdsInSession.add(optionId));
        (current.opening_estimation_ids ?? List<number>()).forEach((oeId: number) =>
          currentOpeningEstimationIdsInSession.add(oeId),
        );
        ((current.document_ids ?? List<number>()) as List<number>).forEach((docId: number) => {
          if (docId < 0) preRestoreNegDocIds.add(docId);
        });
        ((current.image_ids ?? List<number>()) as List<number>).forEach((imgId: number) => {
          if (imgId < 0) preRestoreNegImgIds.add(imgId);
        });
      });

      // 1. Restore each edited existing item from its snapshot (skip drafts — they are removed below).
      snapshotLineItems.forEach((savedLineItem, lineItemId) => {
        if (!draftIds.has(lineItemId)) {
          state = state.setIn(["estimateLineItems", "byId", lineItemId], savedLineItem);
        }
      });

      // 1b. Roll back document/image edits for the restored items via lastSavedById (populated by the
      // canonical RECEIVE_DOCUMENT/RECEIVE_IMAGE flow). Persisted records soft-deleted or display-tweaked
      // during the session are restored; negative-id uploads the snapshot already references are baseline
      // uploads (e.g. ProVia images attached before the session) and are PRESERVED — only recorded so the
      // sweep below skips them. The sweep then deletes the genuinely session-added negatives.
      const docsLastSaved = state.getIn(["documents", "lastSavedById"]) as Map<number, any>;
      const imagesLastSaved = state.getIn(["images", "lastSavedById"]) as Map<number, any>;
      const restoredNegDocIds = new Set<number>();
      const restoredNegImgIds = new Set<number>();
      snapshotLineItems.forEach((savedLineItem, lineItemId) => {
        if (draftIds.has(lineItemId)) return;
        const docIds = (savedLineItem.document_ids ?? List<number>()) as List<number>;
        docIds.forEach((docId: number) => {
          if (docId < 0) {
            restoredNegDocIds.add(docId);
          } else {
            const lastSavedDoc = docsLastSaved?.get(docId);
            if (lastSavedDoc) state = state.setIn(["documents", "byId", docId], lastSavedDoc);
          }
        });
        const imageIds = (savedLineItem.image_ids ?? List<number>()) as List<number>;
        imageIds.forEach((imgId: number) => {
          if (imgId < 0) {
            restoredNegImgIds.add(imgId);
          } else {
            const lastSavedImg = imagesLastSaved?.get(imgId);
            if (lastSavedImg) state = state.setIn(["images", "byId", imgId], lastSavedImg);
          }
        });
      });

      preRestoreNegDocIds.forEach((id: number) => {
        if (!restoredNegDocIds.has(id)) state = state.deleteIn(["documents", "byId", id]);
      });
      preRestoreNegImgIds.forEach((id: number) => {
        if (!restoredNegImgIds.has(id)) state = state.deleteIn(["images", "byId", id]);
      });

      // 2. Remove drafts: unlink from the owning group and delete the record. Session-added negative
      // file records were already swept above.
      draftIds.forEach((draftId) => {
        const draft = estimateLineItemSelector(state, { estimateLineItemId: draftId });
        const groupId = draft?.estimate_group_id;
        if (groupId != null) {
          const group = estimateGroupSelector(state, { estimateGroupId: groupId });
          if (group) {
            const filtered = group.line_item_ids.filter((id: number) => id !== draftId);
            state = state.setIn(["estimateGroups", "byId", groupId, "line_item_ids"], filtered);
          }
        }
        state = state.deleteIn(["estimateLineItems", "byId", draftId]);
      });

      // 3. Restore snapshot options and delete session-added ones (present now but not in the snapshot).
      snapshotOptions.forEach((savedOption, optionId) => {
        state = state.setIn(["estimateLineItemOptions", "byId", optionId], savedOption);
      });
      currentOptionIdsInSession.forEach((optionId) => {
        if (!snapshotOptions.has(optionId)) {
          state = state.deleteIn(["estimateLineItemOptions", "byId", optionId]);
        }
      });

      // 4. Restore snapshot opening estimations (reverts session soft-deletes) and delete new unsaved
      // ones the session added. The restored line item's opening_estimation_ids already point at the
      // snapshot set, so only the byId records need reconciling here.
      snapshotOpeningEstimations.forEach((savedOe, oeId) => {
        state = state.setIn(["openingEstimations", "byId", oeId], savedOe);
      });
      currentOpeningEstimationIdsInSession.forEach((oeId) => {
        if (!snapshotOpeningEstimations.has(oeId)) {
          state = state.deleteIn(["openingEstimations", "byId", oeId]);
        }
      });

      // Group/estimate totals are recomputed after this reducer by the cancelLineItemSession thunk's
      // afterEstimateMutation call (recalculateEstimateTotals is a full, idempotent recompute from
      // byId), so cancel no longer re-sums groups in-reducer — that also re-runs validations, which the
      // old in-reducer recompute never did.

      // Clear all snapshots and the new-draft marker.
      state = state.setIn(["estimates", "editingSnapshotById"], Map());
      state = state.setIn(["estimateGroups", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItems", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItemOptions", "editingSnapshotById"], Map());
      state = state.setIn(["openingEstimations", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItems", "newDraftId"], null);

      return state;
    }
    case COMMIT_LINE_ITEM_SESSION: {
      // Recalculate totals for the committed session's group(s) before clearing snapshots. The editing
      // snapshot is scoped to the line item, so derive the affected group(s) from the snapshotted line
      // item's current record rather than a (no-longer-captured) group snapshot.
      const sessionLineItems = state.getIn(["estimateLineItems", "editingSnapshotById"]) as Map<number, any>;
      const commitGroupIds = new Set<number>();
      sessionLineItems.forEach((_lineItem, lineItemId) => {
        const current = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
        if (current?.estimate_group_id != null) commitGroupIds.add(current.estimate_group_id);
      });
      commitGroupIds.forEach((groupId) => {
        if (estimateGroupSelector(state, { estimateGroupId: groupId })) {
          state = updateNodeAndParents(state, "group", groupId);
        }
      });

      // Clear all editingSnapshotById maps and the new-draft marker after a successful save
      state = state.setIn(["estimates", "editingSnapshotById"], Map());
      state = state.setIn(["estimateGroups", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItems", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItemOptions", "editingSnapshotById"], Map());
      state = state.setIn(["openingEstimations", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItems", "newDraftId"], null);

      return state;
    }
    case CLEANUP_DRAFT_LINE_ITEMS: {
      const lineItemIds = state.getIn(["estimateLineItems", "byId"]).keySeq().toArray() as number[];
      const draftLineItemIds: number[] = [];

      lineItemIds.forEach((id) => {
        if (id < 0) {
          // Clean up child options before removing the line item
          const lineItem = state.getIn(["estimateLineItems", "byId", id]);
          if (lineItem && lineItem.option_ids) {
            lineItem.option_ids.forEach((optionId: number) => {
              state = state.deleteIn(["estimateLineItemOptions", "byId", optionId]);
              state = state.deleteIn(["estimateLineItemOptions", "lastSavedById", optionId]);
            });
          }
          draftLineItemIds.push(id);
          state = state.deleteIn(["estimateLineItems", "byId", id]);
          state = state.deleteIn(["estimateLineItems", "lastSavedById", id]);
        }
      });

      // Remove draft IDs from all group line_item_ids arrays
      if (draftLineItemIds.length > 0) {
        const groupIds = state.getIn(["estimateGroups", "byId"]).keySeq().toArray() as number[];
        groupIds.forEach((groupId) => {
          const group = state.getIn(["estimateGroups", "byId", groupId]);
          if (group && group.line_item_ids) {
            const filteredLineItems = group.line_item_ids.filter((id: number) => id >= 0);
            state = state.setIn(["estimateGroups", "byId", groupId, "line_item_ids"], filteredLineItems);
          }
        });
      }

      // Remove draft estimates (negative IDs) — real estimates were received from the server
      const estimateIds = state.getIn(["estimates", "byId"]).keySeq().toArray() as number[];
      estimateIds.forEach((id) => {
        if (id < 0) {
          state = state.deleteIn(["estimates", "byId", id]);
          state = state.deleteIn(["estimates", "lastSavedById", id]);
        }
      });

      // Remove draft groups (negative IDs) — real groups were received from the server
      const allGroupIds = state.getIn(["estimateGroups", "byId"]).keySeq().toArray() as number[];
      allGroupIds.forEach((id) => {
        if (id < 0) {
          state = state.deleteIn(["estimateGroups", "byId", id]);
          state = state.deleteIn(["estimateGroups", "lastSavedById", id]);
        }
      });

      return state;
    }
    case RECALCULATE_ESTIMATE_TOTALS: {
      // Pure totals recompute: the server response (or an applied template) may leave stale totals;
      // recompute each line item, then all group and estimate-level totals from the current byId data.
      // Does NOT touch lastSavedById — only the save path commits group totals to the baseline (via
      // COMMIT_GROUP_TOTALS_TO_LAST_SAVED), so applying a template still reads dirty as intended.
      const { estimateId } = action.payload;
      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) return state;
      // Level-ordered bottom-up recompute. First recompute each line item exactly once (populates
      // product-only ext_price + the transient option roll-up family, incl. ext_opt_price) — the same
      // updateTotals calculator edits use. On load line items hydrate product-only with no roll-up, so
      // this pass is what makes the subsequent group sweep read correct, option-inclusive values.
      // Implemented as a single pass (NOT updateNodeAndParents("lineItem", …) per item, which would
      // re-walk the group/estimate ancestors once per line item). The group→estimate sweep below is
      // today's code, verbatim.
      estimateRecord.group_ids.forEach((groupId) => {
        const groupRecord = estimateGroupSelector(state, { estimateGroupId: groupId });
        groupRecord?.line_item_ids.forEach((lineItemId) => {
          state = estimateLineItemsReducer(state, EstimateLineItemActions.updateTotals(lineItemId));
        });
      });
      estimateRecord.group_ids.forEach((groupId) => {
        state = updateNodeAndParents(state, "group", groupId);
      });
      return state;
    }
    case COMMIT_GROUP_TOTALS_TO_LAST_SAVED: {
      // Save-path only: sync lastSavedById with the recalculated group totals so the estimate
      // doesn't appear dirty after a save when the server returned stale group subtotals.
      const { estimateId } = action.payload;
      const estimateRecord = estimateSelector(state, { estimateId });
      if (!estimateRecord) return state;
      estimateRecord.group_ids.forEach((groupId) => {
        const updatedGroup = state.getIn(["estimateGroups", "byId", groupId]);
        if (updatedGroup) {
          state = state.setIn(["estimateGroups", "lastSavedById", groupId], updatedGroup);
        }
      });
      return state;
    }
    case RESET_ESTIMATE: {
      // Restore estimate tree from lastSavedById (server state), merging per-record
      // to avoid overwriting other loaded estimates' data.
      const estimatesLastSaved = state.getIn(["estimates", "lastSavedById"]) as Map<number, any>;
      const groupsLastSaved = state.getIn(["estimateGroups", "lastSavedById"]) as Map<number, any>;
      const lineItemsLastSaved = state.getIn(["estimateLineItems", "lastSavedById"]) as Map<number, any>;
      const optionsLastSaved = state.getIn(["estimateLineItemOptions", "lastSavedById"]) as Map<number, any>;
      const openingEstimationsLastSaved = state.getIn(["openingEstimations", "lastSavedById"]) as Map<
        number,
        OpeningEstimationRecord
      >;
      const financeOptionsLastSaved = estimateFinanceOptionsLastSavedById(state);

      // Merge each saved record back into byId
      estimatesLastSaved.forEach((record, id) => {
        state = state.setIn(["estimates", "byId", id], record);
      });
      groupsLastSaved.forEach((record, id) => {
        state = state.setIn(["estimateGroups", "byId", id], record);
      });
      lineItemsLastSaved.forEach((record, id) => {
        state = state.setIn(["estimateLineItems", "byId", id], record);
      });
      optionsLastSaved.forEach((record, id) => {
        state = state.setIn(["estimateLineItemOptions", "byId", id], record);
      });
      openingEstimationsLastSaved.forEach((record, id) => {
        state = state.setIn(["openingEstimations", "byId", id], record);
      });
      financeOptionsLastSaved.forEach((record, id) => {
        state = state.setIn(["estimateFinanceOptions", "byId", id], record);
      });

      // Remove any draft entities (negative IDs) from byId that aren't in lastSavedById
      const currentLineItems = state.getIn(["estimateLineItems", "byId"]) as Map<number, any>;
      currentLineItems.forEach((_record, id) => {
        if (id < 0 && !lineItemsLastSaved.has(id)) {
          state = state.deleteIn(["estimateLineItems", "byId", id]);
        }
      });

      const currentOptions = state.getIn(["estimateLineItemOptions", "byId"]) as Map<number, any>;
      currentOptions.forEach((_record, id) => {
        if (id < 0 && !optionsLastSaved.has(id)) {
          state = state.deleteIn(["estimateLineItemOptions", "byId", id]);
        }
      });

      const currentGroupsForReset = state.getIn(["estimateGroups", "byId"]) as Map<number, any>;
      currentGroupsForReset.forEach((_record, id) => {
        if (id < 0 && !groupsLastSaved.has(id)) {
          state = state.deleteIn(["estimateGroups", "byId", id]);
        }
      });

      const currentOpeningEstimations = state.getIn(["openingEstimations", "byId"]) as Map<
        number,
        OpeningEstimationRecord
      >;
      currentOpeningEstimations.forEach((_record, id) => {
        if (id < 0 && !openingEstimationsLastSaved.has(id)) {
          state = state.deleteIn(["openingEstimations", "byId", id]);
        }
      });

      const currentFinanceOptions = estimateFinanceOptionsById(state);
      currentFinanceOptions.forEach((_record, id) => {
        if (id < 0 && !financeOptionsLastSaved.has(id)) {
          state = state.deleteIn(["estimateFinanceOptions", "byId", id]);
        }
      });

      state = state.setIn(["estimates", "editingSnapshotById"], Map());
      state = state.setIn(["estimateGroups", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItems", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItemOptions", "editingSnapshotById"], Map());
      state = state.setIn(["openingEstimations", "editingSnapshotById"], Map());
      state = state.setIn(["estimateLineItems", "newDraftId"], null);

      // Clear any pending new-estimate auto-open one-shot on navigation-away.
      state = state.setIn(["estimates", "autoOpenLineItemId"], null);

      return state;
    }
    case UPDATE_ESTIMATE_LINE_ITEM_FIELD: {
      const { lineItemId, field, value } = action.payload;

      // Update the field directly on the line item
      state = state.setIn(["estimateLineItems", "byId", lineItemId, field], value);

      // Only recalculate for fields that affect pricing
      // Text fields (name, description) should NOT trigger calculations
      const fieldsRequiringCalculation = [
        "quantity",
        "uom",
        "base_product_price",
        "base_labor_price",
        "product_price",
        "labor_price",
        "price",
        "discountable",
        "markupable",
      ];

      if (fieldsRequiringCalculation.includes(field)) {
        // Before calling updateNodeAndParents, verify that the estimate is loaded
        // This prevents errors when editing draft line items before the estimate is fully loaded
        const lineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
        const group = lineItem ? estimateGroupSelector(state, { estimateGroupId: lineItem.estimate_group_id }) : null;
        const estimate = group ? estimateSelector(state, { estimateId: group.estimate_id }) : null;

        // Only recalculate if we have all required data
        if (lineItem && group && estimate) {
          return updateNodeAndParents(state, "lineItem", lineItemId);
        }

        // If estimate isn't loaded yet, skip calculation
        // Totals will stay at 0 until estimate is available
        return state;
      }

      return state;
    }
    case PATCH_LINE_ITEM_VALIDATIONS: {
      const { items } = action.payload;
      for (const { id, validated, validation_results } of items) {
        state = state.setIn(["estimateLineItems", "byId", id, "validated"], validated);
        state = state.setIn(["estimateLineItems", "byId", id, "validation_results"], validation_results);
      }
      return state;
    }
    case LINE_ITEM_DRAG_END: {
      const { sourceGroupId, destGroupId, oldVisibleIndex, newVisibleIndex } = action.payload;

      const sourceGroup = estimateGroupSelector(state, { estimateGroupId: sourceGroupId });
      const destGroup = estimateGroupSelector(state, { estimateGroupId: destGroupId });
      if (!sourceGroup || !destGroup) return state;

      const lineItems = lineItemsById(state);
      const oldIndex = visibleIndexToFullIndex(sourceGroup.line_item_ids, oldVisibleIndex, lineItems);

      if (sourceGroupId === destGroupId) {
        if (oldVisibleIndex === newVisibleIndex) return state;
        const newIndex = visibleIndexToFullIndex(destGroup.line_item_ids, newVisibleIndex, lineItems);
        return reducer(state, EstimateEditorActions.reorderEstimateLineItem(sourceGroupId, oldIndex, newIndex));
      } else {
        const newIndex = visibleIndexToFullIndex(destGroup.line_item_ids, newVisibleIndex, lineItems);
        return reducer(
          state,
          EstimateEditorActions.moveEstimateLineItem(sourceGroupId, destGroupId, oldIndex, newIndex),
        );
      }
    }
    default:
      return state;
  }
};
